import React from "react";
import { createRoot } from "react-dom/client";
import "./index.scss";
import "mapbox-gl/dist/mapbox-gl.css";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HelmetProvider } from "@dr.pogodin/react-helmet";
import { isClientSideError } from "./Utils/ErrorUtils";
import { userManager, ENVIRONMENT } from "./Utils/EnvUtils";
import {
  createBrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  Navigate,
  Outlet,
  RouterProvider,
  useLocation,
  useNavigationType,
} from "react-router";
import { DialogContextProvider } from "@medoma/components/Dialog/DialogContextProvider";
import { Login as IdsLogin } from "./pages/login/IdsLogin";
import StartPage from "./pages";
import CommandCenterContainer from "./pages/commandcenter/CommandCenterContainer";
import Patients from "./pages/commandcenter/Patients/Patients";
import Patient from "./pages/commandcenter/Patients/Patient/Patient";
import PatientActivities from "./pages/commandcenter/Patients/Patient/Activities/Activities";
import { CareEpisode } from "./pages/commandcenter/Patients/Patient/CareEpisode/CareEpisode";
import { PatientAuthentication } from "./pages/commandcenter/Patients/Patient/Authentication/PatientAuthentication";
import Information from "./pages/commandcenter/Patients/Patient/Information/Information";
import PatientEquipment from "./pages/commandcenter/Patients/Patient/Equipment/Equipment";
import AllocateEquipment from "./pages/commandcenter/Patients/Patient/Equipment/AllocateEquipment";
import { ActivityOccurrenceRoute } from "./pages/commandcenter/Patients/Patient/Activities/ActivityOccurrence/ActivityOccurrence";
import { VideoMeeting as CenterVideo } from "./pages/commandcenter/VideoMeeting";
import { Activities } from "@/pages/commandcenter/Activities";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { AuthProvider } from "react-oidc-context";
import RequireIdsAuth from "./components/RequireAuth/RequireIdsAuth";
import { RequireSiteSelection } from "./components/RequireSiteSelection/RequireSiteSelection";
import { CenterIntercomProvider } from "./components/CenterIntercomProvider";
import { Communication } from "./pages/commandcenter/Patients/Patient/Communication/Communication";
import { Measurements } from "./pages/commandcenter/Patients/Patient/Measurements/Measurements";
import { ActivityOccurrenceDialog } from "./pages/commandcenter/Planning/ActivityOccurrenceDialog/ActivityOccurrenceDialog";
import { ProspectPatients } from "./pages/commandcenter/Patients/PatientsTable/ProspectPatients";
import { DischargedPatients } from "./pages/commandcenter/Patients/PatientsTable/DischargedPatients";
import { AdmittedPatients } from "./pages/commandcenter/Patients/PatientsTable/AdmittedPatients";
import { AdmitPatient } from "./pages/commandcenter/Patients/Patient/AdmitPatient";
import CenterChat from "./pages/commandcenter/Chat/Chat";
import CenterChatWithGo from "./pages/commandcenter/Chat/ChatWithGo";
import * as Sentry from "@sentry/react";
import { Planning } from "./pages/commandcenter/Planning/Planning";
import { PatientNote } from "./pages/commandcenter/Patients/Patient/PatientNote/PatientNote";
import { PatientsContainer } from "./pages/commandcenter/Patients/PatientsContainer";
import { GroupRoute } from "./pages/commandcenter/Patients/Patient/Activities/Group/Group";
import { ActivityOccurrenceInGroupRoute } from "./pages/commandcenter/Patients/Patient/Activities/Group/ActivityOccurrenceInGroupRoute";
import { AllPatients } from "./pages/commandcenter/Patients/PatientsTable/AllPatients";
import { I18nApp } from "./I18nApp";
import { ThemeHelmet } from "./Theme";
import { Trans } from "@lingui/react/macro";
import { Reporting } from "./pages/commandcenter/Reporting/Reporting";
import {
  ToHandleTable,
  PendingTable,
} from "./pages/commandcenter/Reporting/ReportingTable/ReportingTables";
import { Shifts } from "./pages/commandcenter/Shifts/Shifts";
import { ShiftDetails } from "./pages/commandcenter/Shifts/ShiftDetails";
import { GenerateReport } from "./pages/commandcenter/Reporting/GenerateReport";
import RegisterPatient from "./pages/commandcenter/Patients/RegisterPatient/RegisterPatient";
import PatientIdentifierCheck from "./pages/commandcenter/Patients/RegisterPatient/PatientIdentifierCheck";
import { RegisterReturningPatientFormContainer } from "./forms/RegisterPatientForm/RegisterReturningPatientForm";
import { RegisterNewPatientFormContainer } from "./forms/RegisterPatientForm/RegisterNewPatientForm";
import { DischargeSection } from "./pages/commandcenter/Patients/Patient/CareEpisode/Discharge/Discharge";
import NewShift from "./pages/commandcenter/Shifts/NewShift";
import { PauseSection } from "./pages/commandcenter/Patients/Patient/CareEpisode/Pause/Pause";
import { NewActivity } from "./pages/commandcenter/NewActivity/NewActivity";
import { Templates } from "./pages/commandcenter/NewActivity/TemplateManagerTemplates/Templates";
import { CustomActivity } from "./pages/commandcenter/NewActivity/CustomActivity";
import { RetroactiveActivity } from "./pages/commandcenter/NewActivity/RetroactiveActivity";
import { NewActivityDialog } from "./pages/commandcenter/NewActivity/NewActivityDialog";
import { UnpauseSection } from "@/pages/commandcenter/Patients/Patient/CareEpisode/Unpause/Unpause";
import Activity from "./pages/commandcenter/Patients/Patient/Activities/Activity/Activity";
import {
  TemplateManager,
  TemplatesPage,
} from "./pages/template-manager/TemplateManager";
import FeatureFlag from "./components/FeatureFlag/FeatureFlag";
import { knownFeatureFlagsSchema } from "./api/FeatureFlags";
import {
  NewTemplateForm,
  EditTemplateForm,
} from "./pages/template-manager/TemplateForm/TemplateForm";
import {
  NewBatchTemplateForm,
  EditBatchTemplateForm,
} from "./pages/template-manager/BatchTemplateForm/BatchTemplateForm";
import {
  NewGroupForm,
  EditGroupForm,
} from "./pages/template-manager/GroupForm/GroupForm";
import { PausedPatients } from "@/pages/commandcenter/Patients/PatientsTable/PausedPatients";
import { PatientsDailyOverview } from "./pages/commandcenter/Patients/DailyOverview/PatientsDailyOverview";
import { PatientDailyOverview } from "./pages/commandcenter/Patients/DailyOverview/PatientDailyOverview";
import { Equipment } from "./pages/commandcenter/Equipment/Equipment";
import { AssetDetails } from "./pages/commandcenter/Equipment/AssetDetails";
import { ShiftTemplates } from "./pages/commandcenter/Shifts/ShiftTemplates";
import { EquipmentManagement } from "./pages/equipment-management/EquipmentManagement";
import {
  NewCategoryForm,
  EditCategoryForm,
} from "./pages/equipment-management/CategoryForm/CategoryForm";
import { CreateAssetForm } from "./pages/equipment-management/CreateAssetForm";
import { AssetDetails as ManagedAssetDetails } from "./pages/equipment-management/AssetDetails/AssetDetails";
import { GroupInDailyOverview } from "./pages/commandcenter/Patients/DailyOverview/GroupInDailyOverview";
import { ActivityInDailyOverview } from "./pages/commandcenter/Patients/DailyOverview/ActivityInDailyOverview";
import NewShiftFromTemplateForm from "./forms/NewShiftFromTemplateForm";
import NewCustomShiftForm from "./forms/NewCustomShiftForm";
import { CapacityOverview } from "./pages/commandcenter/CapacityOverview/CapacityOverview";

Sentry.init({
  debug: import.meta.env.VITE_SENTRY,
  enabled: import.meta.env.VITE_SENTRY || import.meta.env.PROD,
  dsn: "https://31b1ce0d2b11c2c719991eff12ec36b3@o4511035982479360.ingest.de.sentry.io/4511150471643216",
  environment: ENVIRONMENT,
  // Sentry's equivalent of the v10 defaults: v11 collects more by default, and
  // we have no need for more. User data is only what Sentry.setUser is given,
  // not, for example, the IP address. Opt in per key when a need arises.
  dataCollection: {
    userInfo: false,
    cookies: false,
    httpHeaders: {
      request: { deny: ["forwarded", "-ip", "remote-", "via", "-user"] },
      response: { deny: ["forwarded", "-ip", "remote-", "via", "-user"] },
    },
    httpBodies: [],
    urlQueryParams: { deny: ["forwarded", "-ip", "remote-", "via", "-user"] },
    genAI: { inputs: false, outputs: false },
    databaseQueryData: false,
    graphQL: { document: false, variables: false },
  },
  integrations: [
    Sentry.reactRouterBrowserTracingIntegration({
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.replayIntegration(),
  ],
  // go and center share the org's 5M spans/month quota. In Oct 2026, release
  // health counted ~85K sessions per day when a session still started on
  // every page load and navigation, which is also when a trace starts.
  // Assuming each app sees that many traces and ~100 spans per trace, 1%
  // fills the quota. After a few days, compare actual span usage in Sentry
  // Stats with the quota and adjust.
  tracesSampleRate: 0.01,
  // Session Replay
  replaysSessionSampleRate: 0, // This sets the sample rate at 0%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

if (import.meta.env.DEV && import.meta.env.VITE_MSW) {
  const { worker, HttpNetworkFrame } = await import("./mocks/browser");
  await worker.start({
    onUnhandledFrame({ frame }) {
      if (
        frame instanceof HttpNetworkFrame &&
        frame.data.request.url.includes("medoma")
      ) {
        console.error(
          "Found an unhandled %s request to %s",
          frame.data.request.method,
          frame.data.request.url,
        );
      }
    },
  });
  worker.listHandlers();
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (count, error) => {
        return count < 3 && !isClientSideError(error);
      },
    },
  },
});

const centerChildren = [
  {
    index: true,
    element: <Navigate to="patients" replace />,
  },
  {
    path: "patients",
    element: (
      <PatientsContainer>
        <Outlet />
      </PatientsContainer>
    ),
    children: [
      {
        element: <Patients />,
        children: [
          {
            index: true,
            element: <Navigate to="admitted" replace />,
          },
          {
            path: "admitted",
            element: <AdmittedPatients />,
          },
          {
            path: "paused",
            element: <PausedPatients />,
          },
          {
            path: "discharged",
            element: <DischargedPatients />,
          },
          {
            path: "prospect",
            element: <ProspectPatients />,
          },
          {
            path: "all",
            element: <AllPatients />,
          },
        ],
      },
      {
        path: "register",
        element: <RegisterPatient />,
        children: [
          {
            index: true,
            element: <Navigate to="identifier-check" replace />,
          },
          {
            path: "identifier-check",
            element: <PatientIdentifierCheck />,
          },
          {
            path: "returning",
            element: <RegisterReturningPatientFormContainer />,
          },
        ],
      },
      {
        path: "daily-overview",
        element: <PatientsDailyOverview />,
        children: [
          {
            path: ":patientId",
            element: <Outlet />,
            children: [
              { index: true, element: <PatientDailyOverview /> },
              {
                path: "activities/:activityId/occurrences/:occurrenceId",
                element: <ActivityInDailyOverview />,
              },
              {
                path: "occurrence-groups/:groupId",
                element: <Outlet />,
                children: [
                  { index: true, element: <GroupInDailyOverview /> },
                  {
                    path: "activities/:activityId/occurrences/:occurrenceId",
                    element: <ActivityInDailyOverview />,
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        // Reuses the heading and page frame that the returning-patient form sits in.
        path: "new-prospect",
        element: <RegisterPatient />,
        children: [
          {
            index: true,
            element: <RegisterNewPatientFormContainer />,
          },
        ],
      },
      {
        path: ":patientId",
        element: <Patient />,
        children: [
          {
            index: true,
            element: <Navigate to="activities" replace />,
          },
          {
            path: "admit",
            element: <AdmitPatient />,
          },
          {
            path: "activities",
            element: <PatientActivities />,
            children: [
              {
                path: ":activityId/occurrences/:occurrenceId",
                element: <ActivityOccurrenceRoute />,
              },
              {
                path: "new",
                element: <NewActivity />,
                children: [
                  {
                    index: true,
                    element: <Navigate to="templates" replace />,
                  },
                  {
                    path: "templates",
                    element: <Templates />,
                  },
                  {
                    path: "custom",
                    element: <CustomActivity />,
                  },
                  {
                    path: "retroactive",
                    element: <RetroactiveActivity />,
                  },
                ],
              },
              {
                path: "occurrence-groups/:groupId",
                element: <Outlet />,
                children: [
                  { index: true, element: <GroupRoute /> },
                  {
                    path: "activities/:activityId/occurrences/:occurrenceId",
                    element: <ActivityOccurrenceInGroupRoute />,
                  },
                ],
              },
              {
                path: ":activityId",
                element: <Activity />,
              },
            ],
          },
          {
            path: "information",
            element: <Information />,
          },
          {
            path: "equipment",
            element: <PatientEquipment />,
            children: [
              {
                path: "allocate",
                element: <AllocateEquipment />,
              },
            ],
          },
          {
            path: "communication",
            element: (
              <Communication />
              /* NOTE: If you plan to add more sub routes here, ensure that the alert logic still holds up.
                2023-06-28: const hasChatPanelOpen = pathname.endsWith("communication");
                */
            ),
          },
          {
            path: "care-episode",
            element: <CareEpisode />,
          },
          {
            path: "care-episode/discharge",
            element: <DischargeSection />,
          },
          {
            path: "care-episode/pause",
            element: <PauseSection />,
          },
          {
            path: "care-episode/unpause",
            element: <UnpauseSection />,
          },
          {
            path: "patient-note",
            element: <PatientNote />,
          },
          {
            path: "measurements",
            element: <Measurements />,
          },
          {
            path: "authentication",
            element: <PatientAuthentication />,
          },
        ],
      },
    ],
  },
  {
    path: "activities",
    element: (
      <>
        <Activities />
      </>
    ),
    children: [
      {
        path: "new",
        element: <NewActivity />,
        children: [
          {
            index: true,
            element: <Navigate to="templates" replace />,
          },
          {
            path: "templates",
            element: <Templates />,
          },
          {
            path: "custom",
            element: <CustomActivity />,
          },
          {
            path: "retroactive",
            element: <RetroactiveActivity />,
          },
        ],
      },
      {
        path: ":activityId/occurrences/:occurrenceId",
        element: <ActivityOccurrenceRoute />,
      },

      {
        path: "occurrence-groups/:groupId",
        element: <Outlet />,
        children: [
          { index: true, element: <GroupRoute /> },
          {
            path: "activities/:activityId/occurrences/:occurrenceId",
            element: <ActivityOccurrenceInGroupRoute />,
          },
        ],
      },
    ],
  },
  {
    path: "planning",
    element: (
      <>
        <Planning />
        {/* CAVEAT: This works great for Dialog children, but will not work well for other potential children. */}
        <Outlet />
      </>
    ),
    children: [
      {
        path: "activities/:activityId/occurrences/:occurrenceId",
        element: (
          <ActivityOccurrenceDialog redirectPathRelativeToCenterRootOnClose="planning" />
        ),
      },
      {
        path: "activities/new",
        element: (
          <NewActivityDialog redirectPathRelativeToCenterRootOnClose="planning" />
        ),
        children: [
          {
            index: true,
            element: <Navigate to="templates" replace />,
          },
          {
            path: "templates",
            element: <Templates />,
          },
          {
            path: "custom",
            element: <CustomActivity />,
          },
          {
            path: "retroactive",
            element: <RetroactiveActivity />,
          },
        ],
      },
    ],
  },
  {
    path: "equipment",
    element: <Equipment />,
    children: [
      {
        path: ":assetId",
        element: <AssetDetails />,
      },
    ],
  },
  {
    path: "shifts",
    element: <Shifts />,
    children: [
      {
        path: ":shiftId",
        element: <ShiftDetails />,
      },
      {
        path: "new",
        element: <NewShift />,
        children: [
          { index: true, element: <Navigate to="templates" replace /> },
          {
            path: "templates",
            children: [
              { index: true, element: <ShiftTemplates /> },
              {
                path: ":templateId",
                element: <NewShiftFromTemplateForm />,
              },
            ],
          },
          {
            path: "custom",
            element: <NewCustomShiftForm />,
          },
        ],
      },
    ],
  },
  {
    path: "reporting",
    element: <Reporting />,
    children: [
      {
        index: true,
        element: <Navigate to="to-handle" replace />,
      },
      {
        path: "to-handle",
        element: (
          <>
            <ToHandleTable />
            {/* CAVEAT: This works great for Dialog children, but will not work well for other potential children. */}
            <Outlet />
          </>
        ),
        children: [
          {
            path: "activities/:activityId/occurrences/:occurrenceId",
            element: (
              <ActivityOccurrenceDialog redirectPathRelativeToCenterRootOnClose="reporting/to-handle" />
            ),
          },
        ],
      },
      {
        path: "pending",
        element: (
          <>
            <PendingTable />
            {/* CAVEAT: This works great for Dialog children, but will not work well for other potential children. */}
            <Outlet />
          </>
        ),
        children: [
          {
            path: "activities/:activityId/occurrences/:occurrenceId",
            element: (
              <ActivityOccurrenceDialog redirectPathRelativeToCenterRootOnClose="reporting/pending" />
            ),
          },
        ],
      },
      {
        path: "generate-report",
        element: (
          <>
            <GenerateReport />
            {/* CAVEAT: This works great for Dialog children, but will not work well for other potential children. */}
            <Outlet />
          </>
        ),
      },
    ],
  },
  {
    path: "capacity",
    element: <CapacityOverview />,
  },
  {
    path: "chat",
    element: <CenterChat />,
    children: [
      {
        path: ":employeeId",
        element: <CenterChatWithGo />,
      },
    ],
  },
];

const templateManagerChildren = [
  {
    index: true,
    element: <Navigate to="templates" replace />,
  },
  {
    path: "templates",
    element: <TemplatesPage />,
    children: [
      {
        path: "activity-templates/new",
        element: <NewTemplateForm />,
      },
      {
        path: "activity-templates/:id",
        element: <EditTemplateForm />,
      },
      {
        path: "batch-templates/new",
        element: <NewBatchTemplateForm />,
      },
      {
        path: "batch-templates/:id",
        element: <EditBatchTemplateForm />,
      },
      {
        path: "groups/new",
        element: <NewGroupForm />,
      },
      {
        path: "groups/:id",
        element: <EditGroupForm />,
      },
    ],
  },
];

const router = Sentry.wrapCreateBrowserRouter(createBrowserRouter)([
  {
    path: "/",
    element: (
      <RequireIdsAuth>
        <RequireSiteSelection>
          <CenterIntercomProvider>
            <Outlet />
          </CenterIntercomProvider>
        </RequireSiteSelection>
      </RequireIdsAuth>
    ),
    children: [
      {
        path: "commandcenter",
        element: <CommandCenterContainer />,
        children: centerChildren,
      },
      {
        path: "template-manager",
        element: (
          <FeatureFlag
            feature={
              knownFeatureFlagsSchema.enum.FE_TemplateManagementModuleEnabled
            }
          >
            <TemplateManager />
          </FeatureFlag>
        ),
        children: templateManagerChildren,
      },
      {
        path: "equipment-management",
        element: (
          <FeatureFlag
            feature={knownFeatureFlagsSchema.enum.FE_EquipmentManagement}
          >
            <EquipmentManagement />
          </FeatureFlag>
        ),
        children: [
          {
            index: true,
            element: null,
          },
          {
            path: "categories/new",
            element: <NewCategoryForm />,
          },
          {
            path: "categories/:id",
            element: <EditCategoryForm />,
          },
          {
            path: "assets/new",
            element: <CreateAssetForm />,
          },
          {
            path: "assets/:id",
            element: <ManagedAssetDetails />,
          },
        ],
      },
      {
        path: "*",
        element: <Navigate to="/" />,
      },
      {
        index: true,
        element: <StartPage />,
      },
      {
        path: "video/:patientId",
        element: <CenterVideo />,
      },
    ],
    errorElement: (
      <div>
        <Trans>
          <h1>Något gick fel</h1>
          <p>Ladda om sidan</p>
        </Trans>
      </div>
    ),
  },
  {
    path: "login",
    element: <IdsLogin />,
  },
  {
    path: "status",
    element: <p>OK</p>,
  },
]);

// https://github.com/authts/react-oidc-context/blob/f175dcba6ab09871b027d6a2f2224a17712b67c5/src/AuthProvider.tsx#L20-L30
const removeCodeAndStateParametersFromURL = () =>
  window.history.replaceState({}, document.title, window.location.pathname);

const root = createRoot(document.getElementById("root")!);
root.render(
  <React.StrictMode>
    <AuthProvider
      userManager={userManager}
      onSigninCallback={removeCodeAndStateParametersFromURL}
    >
      <QueryClientProvider client={queryClient}>
        <HelmetProvider>
          <ThemeHelmet />
          <I18nApp>
            <DialogContextProvider>
              <RouterProvider router={router} />
            </DialogContextProvider>
          </I18nApp>
        </HelmetProvider>
        <ReactQueryDevtools
          initialIsOpen={false}
          buttonPosition="bottom-left"
        />
      </QueryClientProvider>
    </AuthProvider>
  </React.StrictMode>,
);
